import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import api from '@/api/_legacy'
import { ApiBusinessUnit, ApiClient, ApiNote, ApiTradingAccount } from '@/api/_legacy/generated-api'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { Fee } from '../../../types'
import { formatFees } from '../../../util'
import { Errors } from '../../../util/validator'

const TENANT_ID = 'absa'

export interface ExistingNoteState {
  businessUnit?: ApiBusinessUnit
  canProceed: boolean

  client?: ApiClient
  errors: Errors
  fees?: Fee[]
  findStatus?: 'inspecting' | 'finding'
  isNoteSelected: boolean
  note?: ApiNote
  selectedNoteTotalValue: number
}

const initialState: ExistingNoteState = {
  canProceed: false,
  errors: {},
  isNoteSelected: false,
  selectedNoteTotalValue: 0
}

export const existingNoteSlice = createSlice({
  name: 'existingNote',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<ApiNote>) => {
      if (action.payload) {
        state.isNoteSelected = true
        state.canProceed = true
        state.note = action.payload
      }
    },
    openNoteSearch: state => {
      state.findStatus = 'finding'
    },
    viewNote: (state, action: PayloadAction<ApiNote>) => {
      state.note = action.payload
      state.fees = formatFees(state.note.fees)
    },
    viewNoteSuccess: (state, action: PayloadAction<ApiClient[]>) => {
      state.findStatus = 'inspecting'
      const client = action.payload.find(x =>
        x.businessUnits.find(y => y.businessUnitId === state.note.businessUnitId)
      )

      const businessUnit = action.payload
        .flatMap(x => x.businessUnits)
        .find(x => x.businessUnitId === state.note.businessUnitId)

      if (!client) {
        throw new Error('Client associated with the note was note found')
      }

      if (!businessUnit) {
        throw new Error('Business unit associated with the note was not found')
      }
      state.client = client
      state.businessUnit = businessUnit
    },
    loadNoteToView: (
      state,
      action: PayloadAction<{ businessUnit: ApiBusinessUnit; client: ApiClient; note: ApiNote }>
    ) => {
      state.findStatus = 'inspecting'
      state.note = action.payload.note
      state.fees = formatFees(state.note.fees)
      state.client = action.payload.client
      state.businessUnit = action.payload.businessUnit
    },
    stopFindingNote: state => {
      state.findStatus = undefined
    },
    goBackToNoteList: state => {
      state.findStatus = 'finding'
    },
    selectNote: state => {
      state.isNoteSelected = true
      state.findStatus = undefined
      state.canProceed = true
    },
    reset: () => initialState
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

const moveNext = () => (dispatch, getState: () => { existingNote: ExistingNoteState }) => {
  const state = getState().existingNote

  dispatch({
    type: 'existingNote/moveNext',
    payload: { note: state.note, client: state.client, businessUnit: state.businessUnit }
  })
}

function* viewNoteSaga() {
  const state: RootState = yield select()
  const { userType } = state.builder
  if (userType === 'admin') {
    try {
      const response = yield call(api.tenant.clientDetail, TENANT_ID)
      yield put({
        type: 'existingNote/viewNoteSuccess',
        payload: response.data
      })
    } catch (error) {
      yield put({ type: 'existingNote/viewNoteFailure', payload: error })
    }
  } else {
    try {
      const response = yield call(api.usermanager.currentList)
      yield put({
        type: 'existingNote/viewNoteSuccess',
        payload: response.data.clients
      })
    } catch (error) {
      yield put({ type: 'existingNote/viewNoteFailure', payload: error })
    }
  }
}

type LoadedState = {
  account: ApiTradingAccount
  businessUnit: ApiBusinessUnit
  client: ApiClient
  note: ApiNote
}

const loadState = (state: LoadedState) => async dispatch => {
  dispatch(
    existingNoteSlice.actions.loadNoteToView({
      note: state.note,
      client: state.client,
      businessUnit: state.businessUnit
    })
  )

  dispatch(existingNoteSlice.actions.selectNote())
}

export const actions = { ...existingNoteSlice.actions, moveNext, loadState }

export type ExistingNoteActions = typeof actions

export function* existingNoteRootSaga() {
  yield all([takeEvery('existingNote/viewNote', viewNoteSaga)])
}

export default existingNoteSlice.reducer
